<template>
  <Admin style="background: #f2f2f2;">
    <v-main>
      <v-container fluid>
        <v-card class="rounded-xl">
          <v-card-text class="tw-p-8">

            <!-- Breadcrumbs -->
            <v-breadcrumbs :items="breadcrumbItems" class="tw-px-0 tw-pt-0 tw-pb-4">
              <template v-slot:divider>
                <v-icon>mdi-chevron-right</v-icon>
              </template>
              <template v-slot:item="{ item }">
                <v-breadcrumbs-item
                  :to="item.to"
                  :disabled="item.disabled"
                  :exact="true"
                >
                  {{ item.text }}
                </v-breadcrumbs-item>
              </template>
            </v-breadcrumbs>

            <h2 class="tw-text-xl tw-font-bold tw-text-black tw-mb-6">จัดการแพ็คเกจลูกค้า</h2>

            <!-- Company Info -->
            <div class="tw-border tw-rounded-xl tw-p-5 tw-mb-6 tw-bg-gray-50 tw-flex tw-items-center tw-gap-4">
              <v-avatar size="48" color="#f5f5f5">
                <img
                  v-if="selectedCompany.imageLogoUrl"
                  :src="selectedCompany.imageLogoUrl"
                  :alt="selectedCompany.nameTH"
                  style="object-fit: contain;"
                />
                <span v-else class="tw-text-xl tw-font-bold tw-text-gray-400">
                  {{ (selectedCompany.nameEN || selectedCompany.nameTH || '?')[0].toUpperCase() }}
                </span>
              </v-avatar>
              <div>
                <div class="tw-text-xs tw-text-gray-400">บริษัทที่เลือก</div>
                <div class="tw-font-bold tw-text-black tw-text-base">{{ selectedCompany.nameTH }}</div>
              </div>
            </div>

            <div v-if="loadingPlan || isProductListLoading" class="tw-flex tw-justify-center tw-py-10">
              <v-progress-circular indeterminate color="#6ec39b" />
            </div>

            <v-alert v-else-if="!isEtax" type="info" outlined class="tw-max-w-md">
              บริษัทนี้ไม่ได้เปิดใช้งาน E-Tax จึงไม่มีการตั้งค่าแพ็คเกจ
            </v-alert>

            <div v-else class="tw-max-w-md">
              <!-- Current Plan -->
              <div class="tw-border tw-rounded-xl tw-p-4 tw-mb-5">
                <div class="tw-text-xs tw-text-gray-400 tw-mb-1">แพ็คเกจปัจจุบัน</div>
                <div class="tw-font-bold tw-text-lg">{{ currentPlanLabel || '-' }}</div>
                <div class="tw-text-xs tw-text-gray-400 tw-mt-1">ID: {{ currentPlanId || '-' }}</div>
              </div>

              <!-- Plan Selector -->
              <v-select
                v-model="newPlanId"
                :items="planItems"
                item-text="text"
                item-value="value"
                label="เลือกแพ็คเกจใหม่"
                outlined
                hide-details
                class="tw-mb-4"
              />

              <!-- Time Start Picker -->
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="timeStartDate"
                    label="วันเริ่มต้นแพ็คเกจ (timeStart)"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    hide-details
                    class="tw-mb-4"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="timeStartDate"
                  no-title
                  @input="dateMenu = false"
                />
              </v-menu>

              <v-btn
                color="#6ec39b"
                dark
                depressed
                :loading="saving"
                :disabled="!newPlanId || (newPlanId === currentPlanId && timeStartDate === originalTimeStartDate)"
                @click="savePlan"
                class="tw-w-full"
              >
                บันทึก
              </v-btn>

              <v-alert v-if="saveSuccess" type="success" dense class="tw-mt-4">
                บันทึกแพ็คเกจเรียบร้อยแล้ว
              </v-alert>
              <v-alert v-if="saveError" type="error" dense class="tw-mt-4">
                เกิดข้อผิดพลาด กรุณาลองใหม่
              </v-alert>
            </div>

          </v-card-text>
        </v-card>
      </v-container>
    </v-main>
  </Admin>
</template>

<script>
import { mapState } from "vuex";
import fc from "@/firebaseConfig";
import Admin from "@/components/admin/admin-main-template";

const PLAN_IDS = [
  "free_month",
  "s_month", "m_month", "l_month", "xl_month", "xxl_month", "xxxl_month", "retail_month", "enterprise_month",
  "s_year", "m_year", "l_year", "xl_year", "xxl_year", "xxxl_year", "retail_year", "enterprise_year",
];

export default {
  name: "SettingPlanManagement",
  components: { Admin },
  computed: {
    ...mapState({
      selectedCompany: (state) => state.companies.selectedCompany,
      paymentPlans: (state) => state.paymentPlans.list,
      selectedCompanyProductList: (state) => state.companies.selectedCompanyProductList,
      isProductListLoading: (state) => state.companies.isProductListLoading,
    }),
    isEtax() {
      return !!(this.selectedCompanyProductList && this.selectedCompanyProductList.isEtax);
    },
    planItems() {
      if (!this.paymentPlans) return [];
      return PLAN_IDS
        .filter((id) => this.paymentPlans[id])
        .map((id) => {
          const plan = this.paymentPlans[id];
          const docs = plan.docsPerMonth ? plan.docsPerMonth.toLocaleString() : "-";
          const suffix = id.endsWith("_year") ? "ต่อปี" : "ต่อเดือน";
          return { value: id, text: `${plan.nameEN || id} — ${docs} ฉบับ/${suffix}` };
        });
    },
    currentPlanLabel() {
      if (!this.currentPlanId || !this.paymentPlans) return null;
      const plan = this.paymentPlans[this.currentPlanId];
      if (!plan) return this.currentPlanId;
      const docs = plan.docsPerMonth ? plan.docsPerMonth.toLocaleString() : "-";
      return `${plan.nameEN || this.currentPlanId} (${docs} ฉบับ)`;
    },
  },
  data: () => ({
    breadcrumbItems: [
      {
        text: "ตั้งค่า",
        disabled: false,
        to: { name: "Company.Settings" },
      },
      {
        text: "จัดการแพ็คเกจลูกค้า",
        disabled: true,
      },
    ],
    currentPlanId: null,
    newPlanId: null,
    timeStartDate: new Date().toISOString().substr(0, 10),
    originalTimeStartDate: new Date().toISOString().substr(0, 10),
    dateMenu: false,
    loadingPlan: false,
    saving: false,
    saveSuccess: false,
    saveError: false,
  }),
  created() {
    this.$store.dispatch("paymentPlans/getList");
    this.$store.dispatch("companies/getCompanyProductList", { companyId: this.$route.params.companyId });
    this.loadPlan();
  },
  watch: {
    selectedCompany() {
      this.loadPlan();
    },
  },
  methods: {
    async loadPlan() {
      const companyId = this.$route.params.companyId;
      if (!companyId) return;
      this.loadingPlan = true;
      this.currentPlanId = null;
      this.newPlanId = null;
      this.saveSuccess = false;
      this.saveError = false;
      try {
        const planSnap = await fc.companies.doc(companyId).collection("PaymentSettings").doc("plan").get();
        if (planSnap.exists) {
          const data = planSnap.data();
          this.currentPlanId = data.paymentPlanID || null;
          this.newPlanId = this.currentPlanId;
          if (data.timeStart) {
            this.timeStartDate = data.timeStart.substr(0, 10);
            this.originalTimeStartDate = data.timeStart.substr(0, 10);
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingPlan = false;
      }
    },
    async savePlan() {
      const companyId = this.$route.params.companyId;
      if (!this.newPlanId || !companyId) return;
      this.saving = true;
      this.saveSuccess = false;
      this.saveError = false;
      try {
        await fc.companies
          .doc(companyId)
          .collection("PaymentSettings")
          .doc("plan")
          .set(
            { paymentPlanID: this.newPlanId, timeStart: new Date(this.timeStartDate).toISOString() },
            { merge: true }
          );
        this.currentPlanId = this.newPlanId;
        this.saveSuccess = true;
      } catch (e) {
        console.error(e);
        this.saveError = true;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
